(() => {
  const ELEMENT_NAME = 'provisions-category-grid';
  const MOBILE_BREAKPOINT = 750;
  const TABLET_BREAKPOINT = 990;

  class ProvisionsCategoryGrid extends HTMLElement {
    constructor() {
      super();

      this.config = null;
      this.items = [];
      this.showMoreButton = null;
      this.showMoreLabel = null;
      this.observer = null;
      this.expandedByViewport = {
        desktop: false,
        tablet: false,
        mobile: false,
      };

      this.handleResize = this.debounce(this.updateVisibleItems.bind(this), 120);
      this.handleShowMore = this.toggleExpanded.bind(this);
      this.handleBlockSelect = this.onBlockSelect.bind(this);
    }

    connectedCallback() {
      if (this.dataset.pcgInitialized === 'true') return;

      this.config = this.readConfig();
      if (!this.config) return;

      this.dataset.pcgInitialized = 'true';
      this.items = Array.from(this.querySelectorAll('[data-pcg-item]'));
      this.showMoreButton = this.querySelector('[data-pcg-show-more]');
      this.showMoreLabel = this.querySelector('[data-pcg-show-more-label]');

      this.applyStyles();
      this.loadSelectedFonts();
      this.bindEvents();
      this.setupEntryAnimation();
      this.updateVisibleItems();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.showMoreButton?.removeEventListener('click', this.handleShowMore);
      this.observer?.disconnect();
    }

    readConfig() {
      const configNode = this.querySelector('[data-provisions-category-grid-config]');
      if (!configNode) return null;

      try {
        return JSON.parse(configNode.textContent);
      } catch (error) {
        console.error('Provisions category grid: invalid configuration.', error);
        return null;
      }
    }

    applyStyles() {
      const styles = this.config?.styles || {};

      Object.entries(styles).forEach(([property, value]) => {
        if (value === null || value === undefined || value === '') return;
        this.style.setProperty(property, String(value));
      });
    }

    async loadSelectedFonts() {
      const fonts = this.config?.fonts;
      if (!fonts?.enabled || !('FontFace' in window)) return;

      await Promise.all([
        this.loadFont(fonts.heading, '--pcg-heading-font-family'),
        this.loadFont(fonts.card, '--pcg-card-font-family'),
      ]);
    }

    async loadFont(font, variableName) {
      if (!font?.family || !font?.url) return;

      const fontStack = `"${font.family}", ${font.fallback || 'sans-serif'}`;
      this.style.setProperty(variableName, fontStack);

      const alreadyLoaded = document.fonts?.check(
        `${font.weight || 400} 1em "${font.family}"`
      );

      if (alreadyLoaded) return;

      try {
        const fontFace = new FontFace(font.family, `url("${font.url}")`, {
          style: font.style || 'normal',
          weight: String(font.weight || 400),
          display: 'swap',
        });

        const loadedFont = await fontFace.load();
        document.fonts.add(loadedFont);
      } catch (error) {
        console.warn(`Provisions category grid: unable to load ${font.family}.`, error);
      }
    }

    bindEvents() {
      window.addEventListener('resize', this.handleResize, { passive: true });
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      this.showMoreButton?.addEventListener('click', this.handleShowMore);
    }

    getViewport() {
      if (window.innerWidth < MOBILE_BREAKPOINT) return 'mobile';
      if (window.innerWidth < TABLET_BREAKPOINT) return 'tablet';
      return 'desktop';
    }

    getItemLimit(viewport) {
      const columns = Number(this.config?.columns?.[viewport] || 1);
      const rows = Number(this.config?.rows?.[viewport] || 0);

      if (rows === 0) return Number.POSITIVE_INFINITY;
      return Math.max(columns * rows, 1);
    }

    updateVisibleItems() {
      const viewport = this.getViewport();
      const limit = this.getItemLimit(viewport);
      const isExpanded = this.expandedByViewport[viewport];
      const hasLimit = Number.isFinite(limit);
      const canToggle = Boolean(
        this.config?.showMore?.enabled && hasLimit && this.items.length > limit
      );

      this.items.forEach((item, index) => {
        item.hidden = !isExpanded && hasLimit && index >= limit;
      });

      if (!this.showMoreButton) return;

      this.showMoreButton.hidden = !canToggle;
      this.showMoreButton.setAttribute('aria-expanded', String(isExpanded));

      if (this.showMoreLabel) {
        this.showMoreLabel.textContent = isExpanded
          ? this.config.showMore.lessLabel
          : this.config.showMore.moreLabel;
      }
    }

    toggleExpanded() {
      const viewport = this.getViewport();
      this.expandedByViewport[viewport] = !this.expandedByViewport[viewport];
      this.updateVisibleItems();

      if (!this.expandedByViewport[viewport]) {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    setupEntryAnimation() {
      const animationType = this.config?.animation?.type;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!animationType || animationType === 'none' || reduceMotion) {
        this.items.forEach((item) => item.classList.add('is-visible'));
        return;
      }

      const stagger = Number(this.config?.animation?.stagger || 0);

      this.items.forEach((item, index) => {
        item.style.setProperty('--pcg-animation-delay', `${index * stagger}ms`);
      });

      if (!('IntersectionObserver' in window)) {
        this.items.forEach((item) => item.classList.add('is-visible'));
        return;
      }

      this.observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        {
          rootMargin: '0px 0px -8% 0px',
          threshold: 0.08,
        }
      );

      this.items.forEach((item) => this.observer.observe(item));
    }

    onBlockSelect(event) {
      const selectedItem = event.target.closest?.('[data-pcg-item]');
      if (!selectedItem || !this.contains(selectedItem)) return;

      const viewport = this.getViewport();
      const itemIndex = this.items.indexOf(selectedItem);
      const limit = this.getItemLimit(viewport);

      if (Number.isFinite(limit) && itemIndex >= limit) {
        this.expandedByViewport[viewport] = true;
        this.updateVisibleItems();
      }

      selectedItem.classList.add('is-visible');
      selectedItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    debounce(callback, delay) {
      let timeoutId;

      return (...args) => {
        window.clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => callback(...args), delay);
      };
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, ProvisionsCategoryGrid);
  }
})();
